import { ApplicationRef, DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ToastController } from '@ionic/angular';
import { interval } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly toastController = inject(ToastController);
  private isUpdatePromptVisible = false;
  private isInstallPromptVisible = false;
  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) {
      return;
    }

    this.registerInstallPromptHandlers();

    this.appRef.isStable
      .pipe(
        first((isStable) => isStable),
        switchMap(() => interval(6 * 60 * 60 * 1000)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.checkForUpdate();
      });

    // Trigger an early update check after app bootstrap.
    void this.checkForUpdate();

    this.swUpdate.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.presentUpdatePrompt();
      });
  }

  private registerInstallPromptHandlers(): void {
    if (this.isRunningStandalone()) {
      return;
    }

    const onBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      this.deferredInstallPrompt = installEvent;
      void this.presentInstallPrompt();
    };

    const onAppInstalled = () => {
      this.deferredInstallPrompt = null;
      this.isInstallPromptVisible = false;
    };

    globalThis.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    globalThis.addEventListener('appinstalled', onAppInstalled);

    this.destroyRef.onDestroy(() => {
      globalThis.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      globalThis.removeEventListener('appinstalled', onAppInstalled);
    });
  }

  private isRunningStandalone(): boolean {
    const isStandaloneDisplay = globalThis.matchMedia?.('(display-mode: standalone)').matches ?? false;
    const isIosStandalone = Boolean((globalThis.navigator as Navigator & { standalone?: boolean }).standalone);
    return isStandaloneDisplay || isIosStandalone;
  }

  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.error('PWA update check failed', error);
    }
  }

  private async presentInstallPrompt(): Promise<void> {
    if (this.isInstallPromptVisible || !this.deferredInstallPrompt) {
      return;
    }
    this.isInstallPromptVisible = true;

    try {
      const toast = await this.toastController.create({
        message: 'Pick a Peak als App installieren?',
        position: 'bottom',
        cssClass: 'pwa-install-toast',
        buttons: [
          {
            text: 'Spater',
            role: 'cancel',
          },
          {
            text: 'Installieren',
            role: 'confirm',
            handler: () => {
              void this.triggerInstallPrompt();
            },
          },
        ],
      });

      await toast.present();
      await toast.onDidDismiss();
    } catch (error) {
      console.error('PWA install prompt failed', error);
    } finally {
      this.isInstallPromptVisible = false;
    }
  }

  private async triggerInstallPrompt(): Promise<void> {
    if (!this.deferredInstallPrompt) {
      return;
    }

    try {
      await this.deferredInstallPrompt.prompt();
      await this.deferredInstallPrompt.userChoice;
    } catch (error) {
      console.error('PWA install action failed', error);
    } finally {
      this.deferredInstallPrompt = null;
    }
  }

  private async presentUpdatePrompt(): Promise<void> {
    if (this.isUpdatePromptVisible) {
      return;
    }
    this.isUpdatePromptVisible = true;

    try {
      const toast = await this.toastController.create({
        message: 'Neue Version verfugbar.',
        position: 'top',
        cssClass: 'pwa-update-toast',
        buttons: [
          {
            text: 'Spater',
            role: 'cancel',
          },
          {
            text: 'Aktualisieren',
            role: 'confirm',
            handler: () => {
              void this.activateAndReload();
            },
          },
        ],
      });

      await toast.present();
      await toast.onDidDismiss();
    } catch (error) {
      console.error('PWA update prompt failed', error);
    } finally {
      this.isUpdatePromptVisible = false;
    }
  }

  private async activateAndReload(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
      globalThis.location.reload();
    } catch (error) {
      console.error('PWA update activation failed', error);
    }
  }
}
